import { ArrowDropDownRounded, ArrowDropUpRounded, CompareRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Autocomplete, Avatar, Box, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useEffect, useMemo, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { ISettlementSchema } from '../../definitions/SettlementSchema'
import Formatter from '../../helpers/Formatter'
import { useAppSelector } from '../../store/Hooks'
import SmartCard from '../controls/cards/SmartCard'
import useCommand from '../hooks/useCommand'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import RoutingManager from '../routing/Routing'
import { snackbar } from '../services/SnackbarService'
import SettlementDetailsAICards from './SettlementDetailsAICards'

const PlusMinusRounded = () => (
    <svg width='10' height='10' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M14.8333 9.66667H10.6667V14C10.5 14 10.2917 14 9.83333 14C9.375 14 9 14 9 14V9.66667H4.83333C4.375 9.66667 4 9.29167 4 8.83333C4 8.375 4.375 8 4.83333 8H9V3.83333C9 3.375 9.375 3 9.83333 3C10.2917 3 10.6667 3.375 10.6667 3.83333V8H14.8333C15.2917 8 15.6667 8.375 15.6667 8.83333C15.6667 9.29167 15.2917 9.66667 14.8333 9.66667Z' fill='white' stroke='white' strokeWidth='1' />
        <path d='M15.0835 16.3333H4.91683C4.4585 16.3333 4.0835 15.9583 4.0835 15.5C4.0835 15.0416 4.4585 14.6666 4.91683 14.6666H15.0835C15.5418 14.6666 15.9168 15.0416 15.9168 15.5C15.9168 15.9583 15.5418 16.3333 15.0835 16.3333Z' fill='white' stroke='white' strokeWidth='1' />
    </svg>

)

const EPSILON = 1e-4

const slotProps: any = {
    loadingOverlay: {
        variant: 'skeleton',
        noRowsVariant: 'skeleton'
    }
}
const sx = {
    'borderStyle': 'none',
    '&.MuiDataGrid-root': {
        paddingTop: 3,
        borderRadius: 2.5
    }, '& .MuiDataGrid-cell:focus': {
        outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    }
}

interface DiffResult {
    type: 'positive' | 'negative' | 'neutral' | 'infinity' | 'added' | 'removed' | 'edited' | 'none'
    value?: number | string
    format?: 'financial' | 'percent'
}

interface ChipProps {
    label: string
    color: 'success' | 'error' | 'info' | 'default'
    icon?: JSX.Element
}

const applyFormat = (val: number | string | null, format?: 'percent' | 'financial') => {
    if (val == null) return null
    if (typeof val !== 'number') return val

    if (format === 'percent') {
        const rounded = Math.round(val * 10000) / 100
        return `${rounded.toFixed(2)} %`
    }

    if (format === 'financial') {
        const abs = Math.abs(val).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        })
        return val < 0 ? `$ (${abs})` : `$ ${abs}`
    }

    return val
}

const extractMetricKey = (item: Record<string, any>) => {
    const keys = Object.keys(item).filter(k => k !== 'format')
    return keys.length === 1 ? keys[0] : null
}

const compareArrays = (arr1: Record<string, number | string | null>[] = [], arr2: Record<string, number | string | null>[] = []) => {
    const toMap = (arr: Record<string, any>[]) => {
        const map = new Map<string, { value: number | string | null, format: 'financial' | 'percent' | null }>()

        for (const item of arr) {
            const key = extractMetricKey(item)
            if (!key) continue

            map.set(key, {
                value: item[key],
                format: (item.format as 'financial' | 'percent') ?? null
            })
        }

        return map
    }

    const map1 = toMap(arr1)
    const map2 = toMap(arr2)

    const allKeys = new Set([...map1.keys(), ...map2.keys()])

    const result = []
    for (const key of allKeys) {
        const a = map1.get(key)
        const b = map2.get(key)

        result.push({
            key,
            current: { value: a?.value ?? null, format: a?.format ?? null },
            selected: { value: b?.value ?? null, format: b?.format ?? null }
        })
    }

    return result
}

const getDiffResult = (current: number | string | null, selected: number | string | null, format?: 'financial' | 'percent'): DiffResult => {
    if (current == null && selected != null) return { type: 'removed' }
    if (current != null && selected == null) return { type: 'added' }

    if (typeof current === 'string' && typeof selected === 'string') return current === selected ? { type: 'neutral' } : { type: 'edited' }

    if (typeof current === 'number' && typeof selected === 'number') {
        const isCurrentZeroish = Math.abs(current) < EPSILON
        const isSelectedZeroish = Math.abs(selected) < EPSILON

        if (isCurrentZeroish && isSelectedZeroish) return { type: 'neutral' }

        if (isCurrentZeroish || isSelectedZeroish) return { type: 'infinity' }

        const isPositiveChange = current > selected

        const diff = format === 'percent'
            ? (current - selected) * 100
            : (Math.abs(current / selected) - 1) * 100

        if (Math.abs(diff) < EPSILON) return { type: 'neutral' }

        return {
            type: isPositiveChange ? 'positive' : 'negative',
            value: +diff.toFixed(1),
            format
        }
    }

    return { type: 'none' }
}

const buildChips = (diff: DiffResult): ChipProps => {
    switch (diff.type) {
        case 'positive':
            return {
                label: `${diff.value}%`,
                color: 'default',
                icon: (
                    <Avatar sx={{ height: 16, width: 16 }}>
                        <ArrowDropUpRounded fontSize='inherit' sx={{ color: 'white' }} />
                    </Avatar>
                )
            }
        case 'negative':
            return {
                label: `${diff.value}%`,
                color: 'default',
                icon: (
                    <Avatar sx={{ height: 16, width: 16 }}>
                        <ArrowDropDownRounded fontSize='inherit' sx={{ color: 'white' }} />
                    </Avatar>
                )
            }
        case 'edited':
            return { label: 'edited', color: 'info' }
        case 'added':
            return { label: 'added', color: 'success' }
        case 'removed':
            return { label: 'removed', color: 'error' }
        case 'infinity':
            return { label: '∞',
                color: 'default',
                icon: (
                    <Avatar sx={{ height: 16, width: 16 }}>
                        <PlusMinusRounded />
                    </Avatar>
                )
            }

        default:
            return null
    }
}

const normalizeSummary = (summary: Record<string, any>[]) => {
    return summary.map(item => {
        const format = item.format ?? null
        const key = Object.keys(item).find(k => k !== 'format')
        return { key, value: item[key], format }
    })
}

const buildSingleRows = (summary: any[]) => {
    if (!summary) return []
    const arr = normalizeSummary(summary)

    return arr.map((item, index) => ({
        id: index,
        key: item.key,
        current: applyFormat(item.value, item.format)
    }))
}

const buildComparisonRows = (summaryA: any[], summaryB: any[]) => {
    const compared = compareArrays(summaryA, summaryB)

    return compared.map((item, index) => {
        const diff = getDiffResult(
            item.current.value,
            item.selected.value,
            item.current.format ?? item.selected.format ?? undefined
        )

        return {
            id: index,
            key: item.key,
            current: applyFormat(item.current.value, item.current.format),
            selected: applyFormat(item.selected.value, item.selected.format),
            diff
        }
    })
}

const singleColumns: GridColDef[] = [
    { field: 'key', headerName: 'Metric', flex: 1 },
    { field: 'current', headerName: 'Value', flex: 1 }
]

const formatPeriod = (option: Pick<ISettlementSchema, 'type' | 'periodStart' | 'periodEnd' | 'uuid' | 'contractId' | 'runAt'>) => `${Formatter.date(new Date(option.periodStart), 'mm/dd/yyyy')} - ${Formatter.date(new Date(option.periodEnd), 'mm/dd/yyyy')}`

const formatTimestamp = (runAt: string) => Formatter.date(new Date(runAt), 'mm/dd/yyyy hh:mm:ss')

const SettlementDetailsTable = () => {
    const params = RoutingManager.getParams<{ settlementId: string }>(useAppSelector(s => s.session.routeParams))

    const [selectedContractId, setSelectedContractId] = useState('')
    const [selectedSettlementUUID, setSelectedSettlementUUID] = useState('')
    const [selectedPeriod, setSelectedPeriod] = useState<string>('')
    const [rows, setRows] = useState([])
    const [columns, setColumns] = useState(singleColumns)
    const [isComparison, setIsComparison] = useState(false)
    const [selectedSettlementSummary, setSelectedSettlementSummary] = useState(null)
    const userUUID = useAppSelector(s => s.app.currentUser.uuid)

    const { response: userQuery } = useQuery({
        queryKey: buildQueryKey('getUserInfo'),
        queryFn: () => ApiClient.getUserInfo(userUUID),
        cache: false
    })

    const { status: contractQueryStatus, response: contractQuery } = useQuery({
        queryKey: buildQueryKey('getHeadContractNames'),
        queryFn: () => ApiClient.getHeadContractNames(),
        cache: true
    })

    const { status: currentSettlementStatus, response: currentSettlementResponse } = useQuery({
        queryKey: buildQueryKey('settlement-details', params.settlementId),
        queryFn: () => ApiClient.getSettlementDetails(params.settlementId),
        cache: true
    })

    const { status: allSettlementStatus, response: allSettlementResponse } = useQuery({
        queryKey: buildQueryKey('getSettlementsByContractId' + selectedContractId),
        queryFn: () => FlowApiClient.getSettlementsByContractId(Number(selectedContractId)),
        cache: false,
        enabled: !!selectedContractId
    })

    const comparisonColumns: GridColDef[] = useMemo(() => ([
        { field: 'key', headerName: 'Metric', flex: 1 },
        { field: 'current', headerName: currentSettlementResponse?.settlement?.contractName ?? 'Current', flex: 1 },
        { field: 'selected', headerName: contractQuery?.find(c => c.id === Number(selectedContractId))?.name ?? 'Selected', flex: 1 },
        {
            field: 'diffChip',
            headerName: 'Δ vs selected',
            flex: 1,
            renderCell: ({ row }) => {
                const chipProps = buildChips(row.diff)
                return row.diff.type !== 'neutral' ? <Chip size='small' {...chipProps} /> : '-'
            }
        }
    ]), [currentSettlementResponse, contractQuery, selectedContractId])

    const { execute: getSelectedSettlementSummary, status: getSelectedSettlementSummaryStatus } = useCommand({
        commandFn: (settlementUUID: string) => FlowApiClient.getSettlementSummaryByUUID(settlementUUID),
        onFinish: result => {
            if (result.status === 'success') {
                setSelectedSettlementSummary(result.response)
                const comparison = buildComparisonRows(currentSettlementResponse.settlement.summary, result.response.summary)
                setRows(comparison)
                setColumns(comparisonColumns)
                setIsComparison(true)
            }
            if (result.status === 'error') {
                console.error('Error approving request:', result.errorText)
                snackbar.show({
                    severity: 'error',
                    text: 'Failed to get the settlement summary: ' + result.errorText
                })
            }
        }
    })

    useEffect(() => {
        if (currentSettlementResponse?.settlement?.summary) {
            const singleRows = buildSingleRows(currentSettlementResponse.settlement.summary)
            setRows(singleRows)
        }
    }, [currentSettlementResponse])

    useEffect(() => {
        if (currentSettlementResponse?.settlement?.contractId)
            setSelectedContractId(currentSettlementResponse.settlement.contractId)
    }, [currentSettlementResponse])

    const settlementOptions: Pick<ISettlementSchema, 'uuid' | 'type' | 'periodEnd' | 'periodStart' | 'contractId' | 'runAt'>[] = allSettlementResponse ?? []

    const availablePeriods = [...new Set(settlementOptions.map(option => formatPeriod(option)))]

    const filteredSettlements = selectedPeriod
        ? settlementOptions.filter(option => formatPeriod(option) === selectedPeriod)
        : settlementOptions

    const selectedSettlement = settlementOptions.find(settlement => settlement.uuid === selectedSettlementUUID) ?? null

    return (
        <>
            <Stack spacing={6} height='100%'>
                <SmartCard>
                    <Stack spacing={1}>
                        <Stack flexDirection='row' alignItems='center' justifyContent='space-between' gap={1}>
                            <Typography variant='h6'>Performance Details</Typography>
                            <Stack flexDirection='row' alignItems='center' gap={1}>
                                <Autocomplete
                                    sx={{ width: 300 }}
                                    size='small'
                                    options={contractQuery ?? []}
                                    getOptionLabel={option => option.name}
                                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                                    value={contractQuery?.find(c => c.id === Number(selectedContractId)) ?? null}
                                    loading={contractQueryStatus === 'loading'}
                                    onChange={(_, selected) => {
                                        setSelectedContractId(String(selected?.id ?? ''))
                                        setSelectedSettlementUUID('')
                                        setSelectedPeriod('')
                                        setIsComparison(false)
                                        setSelectedSettlementSummary(null)
                                        const singleRows = buildSingleRows(currentSettlementResponse.settlement.summary)
                                        setRows(singleRows)
                                        setColumns(singleColumns)
                                    }}
                                    renderOption={(props, option) => {
                                        return (
                                            <li {...props} key={option.uuid}>
                                                {option.name}
                                            </li>
                                        )
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label='Contract'
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {contractQueryStatus === 'loading' && <CircularProgress color='inherit' size={18} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                                <Autocomplete
                                    sx={{ width: 230 }}
                                    size='small'
                                    disabled={!selectedContractId || allSettlementStatus !== 'success'}
                                    options={availablePeriods}
                                    value={selectedPeriod || null}
                                    loading={allSettlementStatus === 'loading'}
                                    onChange={(_, value) => {
                                        setSelectedPeriod(value ?? '')
                                        setSelectedSettlementUUID('')
                                        const singleRows = buildSingleRows(currentSettlementResponse.settlement.summary)
                                        setColumns(singleColumns)
                                        setRows(singleRows)
                                        setIsComparison(false)
                                        setSelectedSettlementSummary(null)
                                    }}
                                    renderOption={(props, option) => {
                                        return (
                                            <li {...props} key={option}>
                                                {option}
                                            </li>
                                        )
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label='Period'
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>

                                                        {allSettlementStatus === 'loading' && (
                                                            <CircularProgress
                                                                color='inherit'
                                                                size={18}
                                                            />
                                                        )}

                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                                <Autocomplete
                                    sx={{ width: 230 }}
                                    size='small'
                                    disabled={!selectedPeriod || allSettlementStatus !== 'success'}
                                    options={filteredSettlements}
                                    getOptionLabel={option =>
                                        formatTimestamp(option.runAt)}
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid}
                                    value={selectedSettlement}
                                    loading={allSettlementStatus === 'loading'}
                                    onChange={(_, selected) => {
                                        setSelectedSettlementUUID(selected?.uuid ?? '')
                                        const singleRows = buildSingleRows(currentSettlementResponse.settlement.summary)
                                        setColumns(singleColumns)
                                        setRows(singleRows)
                                        setIsComparison(false)
                                        setSelectedSettlementSummary(null)
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.uuid}>
                                            {formatTimestamp(option.runAt)}
                                        </li>
                                    )}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label='Timestamp'
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>

                                                        {allSettlementStatus === 'loading' && (
                                                            <CircularProgress
                                                                color='inherit'
                                                                size={18}
                                                            />
                                                        )}

                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                                <LoadingButton
                                    variant='outlined'
                                    startIcon={<CompareRounded />}
                                    loadingPosition='start'
                                    loading={getSelectedSettlementSummaryStatus === 'loading'}
                                    disabled={!selectedContractId || !selectedSettlementUUID}
                                    color={selectedSettlementSummary ? 'error' : 'primary'}
                                    onClick={() => {
                                        if (selectedSettlementSummary) {
                                            const singleRows = buildSingleRows(currentSettlementResponse.settlement.summary)
                                            setRows(singleRows)
                                            setColumns(singleColumns)
                                            setSelectedSettlementUUID('')
                                            setSelectedSettlementSummary(null)
                                            setIsComparison(false)
                                            return
                                        }
                                        getSelectedSettlementSummary(selectedSettlementUUID)
                                    }}>{selectedSettlementSummary ? 'Stop Comparing' : 'Compare'}
                                </LoadingButton>
                            </Stack>
                        </Stack>
                        <DataGrid
                            sx={sx}
                            slotProps={slotProps}
                            loading={currentSettlementStatus === 'loading' || getSelectedSettlementSummaryStatus === 'loading'}
                            rows={rows}
                            columns={columns}
                            disableRowSelectionOnClick
                            hideFooter
                        />
                    </Stack>
                </SmartCard>
                <Box flex={2}>
                    <SettlementDetailsAICards settlementData={{ single: currentSettlementResponse?.settlement?.uuid, comparison: selectedSettlementUUID, isComparison }} userInfo={userQuery} />
                </Box>
            </Stack>
        </>
    )
}

export default SettlementDetailsTable
